import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { PRIORITIES } from '../useHelpDesk';

const COLORS = {
  border: '#cee2d6',
  text: '#362b21',
  textSoft: '#1c211f',
  accent: '#0C9344',
  accentSoft: '#cbebd9',
};

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'image'],
    ['clean'],
  ],
};

const useStyles = makeStyles(() => ({
  title: {
    fontWeight: 700,
    fontSize: '1.15rem',
    color: COLORS.text,
    borderBottom: `1px solid ${COLORS.border}`,
  },
  content: {
    paddingTop: 20,
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
  },
  fieldRow: {
    display: 'flex',
    gap: 14,
  },
  label: {
    fontSize: '0.78rem',
    fontWeight: 700,
    color: COLORS.textSoft,
    marginBottom: 6,
  },
  editorWrapper: {
    '& .ql-toolbar.ql-snow': {
      borderColor: COLORS.border,
      borderRadius: '8px 8px 0 0',
    },
    '& .ql-container.ql-snow': {
      borderColor: COLORS.border,
      borderRadius: '0 0 8px 8px',
      minHeight: 140,
      fontSize: '0.85rem',
    },
  },
  submitBtn: {
    backgroundColor: COLORS.accent,
    color: '#fff',
    textTransform: 'none',
    fontWeight: 600,
    '&:hover': { backgroundColor: '#a05716' },
    '&:disabled': { backgroundColor: COLORS.accentSoft, color: COLORS.textSoft },
  },
  cancelBtn: {
    textTransform: 'none',
    color: COLORS.textSoft,
  },
}));

const EMPTY_FORM = {
  taskName: '',
  statusId: '',
  priorityId: 3,
  taskDueDate: '',
  taskDescription: '',
};

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {Array} props.statuses
 * @param {boolean} props.isSubmitting
 * @param {(payload: Object) => void} props.onSubmit
 */
function AddEditHelpDeskTaskModal({ open, onClose, statuses, isSubmitting, onSubmit }) {
  const classes = useStyles();
  const [form, setForm] = useState(EMPTY_FORM);
  const [nameError, setNameError] = useState(false);

  // Reset the form each time the modal opens, and default status to the
  // first available column once statuses have loaded.
  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY_FORM, statusId: statuses[0]?.id ?? '' });
      setNameError(false);
    }
  }, [open, statuses]);

  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    if (field === 'taskName' && nameError) setNameError(false);
  };

  const handleDescriptionChange = (value) => {
    setForm((prev) => ({ ...prev, taskDescription: value }));
  };

  const handleSubmit = () => {
    if (!form.taskName.trim()) {
      setNameError(true);
      return;
    }
    onSubmit({
      taskName: form.taskName.trim(),
      taskDescription: form.taskDescription,
      statusId: Number(form.statusId),
      priorityId: Number(form.priorityId),
      taskDueDate: form.taskDueDate ? new Date(form.taskDueDate).toISOString() : null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.title}>New Help Desk Task</DialogTitle>

      <DialogContent className={classes.content}>
        <Box>
          <TextField
            label="Task name"
            variant="outlined"
            placeholder="e.g. Reset password for client"
            value={form.taskName}
            onChange={handleChange('taskName')}
            error={nameError}
            helperText={nameError ? 'Give the task a name.' : ' '}
            fullWidth
            autoFocus
          />
        </Box>

        <Box className={classes.fieldRow}>
          <TextField
            select
            label="Status"
            variant="outlined"
            value={form.statusId}
            onChange={handleChange('statusId')}
            fullWidth
          >
            {statuses.map((status) => (
              <MenuItem key={status.id} value={status.id}>
                {status.statusName}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Priority"
            variant="outlined"
            value={form.priorityId}
            onChange={handleChange('priorityId')}
            fullWidth
          >
            {PRIORITIES.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <TextField
          label="Due date"
          variant="outlined"
          type="date"
          value={form.taskDueDate}
          onChange={handleChange('taskDueDate')}
          InputLabelProps={{ shrink: true }}
          style={{ maxWidth: 220 }}
        />

        <Box>
          <div className={classes.label}>Description</div>
          <div className={classes.editorWrapper}>
            <ReactQuill
              theme="snow"
              value={form.taskDescription}
              onChange={handleDescriptionChange}
              modules={QUILL_MODULES}
              placeholder="Add any details, steps to reproduce, or screenshots…"
            />
          </div>
        </Box>
      </DialogContent>

      <DialogActions style={{ padding: '16px 24px' }}>
        <Button className={classes.cancelBtn} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          className={classes.submitBtn}
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Creating…' : 'Create task'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddEditHelpDeskTaskModal;